import { Decimal } from 'decimal.js';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCentsToDollars(cents: number) {
  const dollars = new Decimal(cents).div(100);
  // Use Intl.NumberFormat for proper currency formatting
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    // Avoid unnecessary decimals like $10.00, show $10 instead
    minimumFractionDigits: dollars.mod(1).equals(0) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(dollars.toNumber()); // Format the number directly
}

export function serializeStringToSlug(str: string) {
  return str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

export function deserializeSlugToString(slug: string) {
  return slug.replace(/-/g, ' ');
}

// Helper function to apply ordering based on saved IDs
// Ensure the return type matches the input item type
export const applyOrderSettings = <T extends { id: string }>(
  items: T[] | null | undefined, // Allow null/undefined input
  orderedIds?: string[] | null | undefined // Allow null/undefined IDs
): T[] => {
  const validItems = items || []; // Use empty array if items is null/undefined
  if (!orderedIds || orderedIds.length === 0 || validItems.length === 0) {
    return validItems; // Return original items (or empty array)
  }

  // Create a map for efficient lookup
  const itemMap = new Map(validItems.map((item) => [item.id, item]));

  // Build the ordered list, preserving the original item type
  const ordered = orderedIds
    .map((id) => itemMap.get(id)) // Get items by ID
    .filter((item): item is T => !!item); // Filter out undefined and assert type T

  // Find items not in the saved order (new items)
  const newItems = validItems.filter((item) => !orderedIds.includes(item.id));

  // Combine ordered items and new items
  return [...ordered, ...newItems];
};
